import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import WineModal from "../components/WineModal";
import Dessert from "../images/dessert.png";
import CornerLadybug from "../images/corner/corner-ladybug-art.webp";
import Logo from "../images/logo-dark.svg";
import SheWolf from "../images/shewolf-art.png";
import Starters from "../images/starters.png";
import SoupSalad from "../images/soupsalad.png";
import Pizza from "../images/menu-pizza.png";
import Piadine from "../images/piadine.png";
import Affogato from "../images/menu-affogato.png";
import WineItem from "../components/WineItem";
import Section, { getMenuCategories } from "../components/Section";
import FullPageLoader from "../components/FullPageLoader";
import ReservationFinder from "../components/ReservationFinder";

const MENU_JUMP_LINKS = [
  { id: "wine", label: "Wine" },
  { id: "starters", label: "Starters" },
  { id: "soup-salad", label: "Soup & Salad" },
  { id: "piadine", label: "Piadine" },
  { id: "pizza", label: "Pizza" },
  { id: "dessert", label: "Desserts" },
  { id: "drinks", label: "Coffee" },
];

const scrollToSection = (event, id) => {
  event.preventDefault();
  const el = document.getElementById(id);
  if (!el) return;
  const prefersReduced = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;
  el.scrollIntoView({
    behavior: prefersReduced ? "auto" : "smooth",
    block: "start",
  });
};

const Menu = ({ menuItems, cellarWines, hours, title }) => {
  const [wineModalInfo, setWineModalInfo] = useState(false);

  useEffect(() => {
    document.title = `Auburn Road - ${title}`;
    return () => {
      document.title = "Auburn Road Vineyards";
    };
  }, [title]);

  if (!menuItems || !hours) {
    return <FullPageLoader />;
  }

  const winesObj = {};
  menuItems.forEach((item) => {
    const categories = getMenuCategories(item);
    if (!categories.length) return;

    const secCat =
      item.secondary_category === "sweetFruit"
        ? "sweet"
        : item.secondary_category;
    categories.forEach((cat) => {
      if (cat === "Wine") {
        if (!secCat) return;
        if (!winesObj[secCat]) {
          winesObj[secCat] = [];
        }
        winesObj[secCat].push(item);
      }
    });
  });

  const renderCellarWines = () => {
    if (!cellarWines || cellarWines.length === 0) return null;

    return cellarWines.map((item) => {
      const { name, vintage, description, price, soldOut } = item;
      return (
        <article
          className={`menu-item${soldOut ? " menu-item--soldout" : ""}`}
          key={item.id || name}
        >
          <div className="menu-item__header">
            <h3 className="menu-item__name">
              {name}
              {vintage && <span className="menu-item__vintage">{vintage}</span>}
            </h3>
            <span className="menu-item__price">
              {soldOut ? "sold out" : price != null ? `$${price}` : null}
            </span>
          </div>
          {description && <p className="menu-item__desc">{description}</p>}
        </article>
      );
    });
  };

  const showWines = (wines) => {
    if (!wines || wines.length === 0) return null;

    return wines.map((wine) => (
      <WineItem key={wine.id} wine={wine} setWineModalInfo={setWineModalInfo} />
    ));
  };

  const WineGroup = ({ id, title, swipe, wines }) => {
    const items = showWines(wines);
    if (!items) return null;

    return (
      <div className="menu-wine-group">
        <h3
          className={`menu-wine-group__title menu-wine-group__title--${swipe}`}
          id={id}
        >
          {title}
        </h3>
        <div className="menu-item-grid">{items}</div>
      </div>
    );
  };

  const aLaCarte = (
    <div className="menu-extras">
      <div className="menu-extras__title">A la carte</div>
      {[
        ["Artisan cheese", "5"],
        ["Roasted red peppers", "3.5"],
        ["Hummus", "4"],
        ["Bowl of Spanish nuts", "4"],
        ["Gluten free crackers", "2.5"],
        ["Crostini crackers", "5"],
        ["Woodfire house-baked flatbread", "4.5"],
        ["Castelvetrano olives", "4.5"],
      ].map(([label, price]) => (
        <div className="menu-extras__row" key={label}>
          <span>{label}</span>
          <span className="menu-item__price">{price}</span>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <img className="corner-ladybug" src={CornerLadybug} alt="" />
      <div className="container menu-page p-t-100">
        <section className="menu-hero">
          <div>
            <p className="menu-hero__kicker">Auburn Road Vineyards</p>
            <h1 className="huge-text-no-quote mb-3">Menu</h1>
            <p className="menu-hero__intro">
              Wine by the glass and bottle, wood-fired pizza, and
              Italian-inspired plates, served in the vineyard.
            </p>
          </div>
          <div className="menu-hero__card">
            <p className="menu-hero__card-title">Visiting notes</p>
            <p>A 2.5% discount if you pay with cash.</p>
            <p>Automatic 20% gratuity on parties of 5 or more.</p>
          </div>
        </section>

        <div className="m-b-40 m-t-30">
          <ReservationFinder />
        </div>

        <nav className="menu-jump" aria-label="Menu sections">
          {MENU_JUMP_LINKS.map(({ id, label }) => (
            <a
              className="menu-jump__link"
              href={`#${id}`}
              key={id}
              onClick={(event) => scrollToSection(event, id)}
            >
              {label}
            </a>
          ))}
        </nav>

        <section className="menu-wine" id="wine">
          <div className="menu-block__heading">
            <h2 className="huge-text-no-quote">Wine</h2>
          </div>
          {winesObj.flights?.length > 0 && (
            <div className="menu-item-grid m-b-30">
              {showWines(winesObj.flights)}
            </div>
          )}
          <div className="menu-wine-groups">
            <WineGroup
              id="white"
              title="Dry white and blush"
              swipe="swipe-2"
              wines={winesObj.white}
            />
            <WineGroup
              id="sweet"
              title="Sweet and fruit"
              swipe="swipe"
              wines={winesObj.sweet}
            />
            <WineGroup
              id="sparkling"
              title="Sparkling"
              swipe="swipe-2"
              wines={winesObj.sparkling}
            />
            <WineGroup
              id="red"
              title="Dry red"
              swipe="swipe"
              wines={winesObj.red}
            />
            <WineGroup
              id="fortified"
              title="Fortified wines"
              swipe="swipe-2"
              wines={winesObj.fortified}
            />
            {cellarWines && cellarWines.length > 0 && (
              <div className="menu-wine-group" id="cellar">
                <h3 className="menu-wine-group__title menu-wine-group__title--swipe">
                  Cellar wines
                </h3>
                <p className="menu-cellar__note">
                  Available to Wine CSA members and Guided Premium Tasting
                  guests. Bottle sales only.
                </p>
                <div className="menu-item-grid">{renderCellarWines()}</div>
              </div>
            )}
          </div>
        </section>

        <Modal
          show={wineModalInfo}
          component={WineModal}
          closeFn={() => setWineModalInfo(false)}
          content={wineModalInfo}
        />

        <section className="menu-block" id="starters">
          <div className="menu-block__copy">
            <div className="menu-block__heading">
              <p className="menu-hero__kicker">to begin</p>
              <h2 className="huge-text-no-quote">Starters</h2>
            </div>
            <Section
              items={menuItems}
              category="Starter"
              lastItem={() => aLaCarte}
            />
          </div>
          <div className="menu-block__media menu-pizza">
            <img className="menu-photo" src={Starters} alt="starters" />
          </div>
        </section>

        <section className="menu-block" id="soup-salad">
          <div className="menu-block__copy">
            <div className="menu-block__heading">
              <p className="menu-hero__kicker">Seasonal</p>
              <h2 className="huge-text-no-quote">Soup & Salad</h2>
            </div>
            <Section items={menuItems} category="Soup Salad" />
          </div>
          <div className="menu-block__media menu-pizza">
            <img className="menu-photo" src={SoupSalad} alt="soup and salad" />
          </div>
        </section>

        <section className="menu-block" id="piadine">
          <div className="menu-block__copy">
            <div className="menu-block__heading">
              <p className="menu-hero__kicker">Italian street sandwiches</p>
              <h2 className="huge-text-no-quote">Piadine</h2>
            </div>
            <p className="menu-block__intro">
              House-baked bread from our wood-fired oven, filled and served
              warm.
            </p>
            <Section items={menuItems} category="Piadine" />
          </div>
          <div className="menu-block__media menu-piadine">
            <img className="menu-photo" src={Piadine} alt="piadine" />
          </div>
        </section>

        <section className="menu-block" id="pizza">
          <div className="menu-block__copy">
            <div className="menu-block__heading">
              <p className="menu-hero__kicker">From the oven</p>
              <h2 className="huge-text-no-quote">Wood-fired Pizza</h2>
            </div>
            <div className="menu-note-card m-b-30">
              <p className="menu-note-card__title">Available only on</p>
              {hours?.pizza ? (
                hours.pizza.map((x) => (
                  <p className="hours-item" key={x.key || x.days}>
                    <span className="b">{x.days}: </span>
                    {x.hours}
                  </p>
                ))
              ) : (
                <p>Loading hours...</p>
              )}
            </div>
            <Section
              items={menuItems}
              category="Pizza"
              lastItem={() => (
                <div className="menu-extras">
                  <div className="menu-extras__title">Add-ons</div>
                  <div className="menu-extras__row">
                    <span>Prosciutto, pepperoni, or sausage</span>
                    <span className="menu-item__price">2.5</span>
                  </div>
                  <div className="menu-extras__row">
                    <span>Mushroom or onion</span>
                    <span className="menu-item__price">1.5</span>
                  </div>
                </div>
              )}
            />
          </div>
          <div className="menu-block__media">
            <img className="menu-photo menu-pizza" src={Pizza} alt="pizza" />
          </div>
        </section>
      </div>

      <img src={SheWolf} className="menu-shewolf" alt="" />
      <div className="container menu-page">
        <section className="menu-block" id="dessert">
          <div className="menu-block__copy">
            <div className="menu-block__heading">
              <p className="menu-hero__kicker">Something sweet</p>
              <h2 className="huge-text-no-quote">Desserts</h2>
            </div>
            <Section items={menuItems} category="Dessert" compact />
          </div>
          <div className="menu-block__media">
            <img
              className="menu-photo menu-dessert"
              src={Dessert}
              alt="auburn road dessert"
            />
          </div>
        </section>

        <section className="menu-block" id="drinks">
          <div className="menu-block__copy">
            <div className="menu-block__heading">
              <p className="menu-hero__kicker">After pizza</p>
              <h2 className="huge-text-no-quote">Coffee and other beverages</h2>
            </div>
            <Section items={menuItems} category="Coffee" compact />
          </div>
          <div className="menu-block__media">
            <img
              className="menu-photo menu-coffee"
              src={Affogato}
              alt="affogato"
            />
          </div>
        </section>
      </div>
    </>
  );
};

export default Menu;
